import React, { useEffect } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useModelStore } from "../../../../store/useModelStore";
import type { ViewType } from "../../utils/workspace.types";

/**
 * Camera Aware Selection component - handles selection with camera projection
 * Enhanced for multi-view support and workspace freezing during selection
 */
interface CameraAwareSelectionProps {
  currentView: ViewType;
}

export const CameraAwareSelection: React.FC<CameraAwareSelectionProps> = ({
  currentView,
}) => {
  const { camera, gl, controls } = useThree();
  const modelStore = useModelStore();
  const {
    nodes,
    beams,
    toggleNodeSelection,
    toggleBeamSelection,
    clearSelection,
  } = modelStore;

  // Multi-view projection function - always use proper camera projection
  const projectToScreenMultiView = (
    worldPosition: THREE.Vector3
  ): { x: number; y: number } => {
    const rect = gl.domElement.getBoundingClientRect();

    // Always use the camera's projection matrix for accurate coordinate mapping
    // This works for all view types including orthographic projections
    const vector = worldPosition.clone().project(camera);

    // Convert from normalized device coordinates (-1 to 1) to screen coordinates
    const screenX = ((vector.x + 1) * rect.width) / 2;
    const screenY = ((-vector.y + 1) * rect.height) / 2;

    return { x: screenX, y: screenY };
  };

  // Check if beam is completely contained in rectangle (STAAD.Pro style)
  const isBeamCompletelyContained = (
    beam: any,
    bounds: { left: number; right: number; top: number; bottom: number }
  ): boolean => {
    const startNode = nodes.find((n) => n.id === beam.startNodeId);
    const endNode = nodes.find((n) => n.id === beam.endNodeId);

    if (!startNode || !endNode) return false;

    const startWorld = new THREE.Vector3(startNode.x, startNode.y, startNode.z);
    const endWorld = new THREE.Vector3(endNode.x, endNode.y, endNode.z);

    const startScreen = projectToScreenMultiView(startWorld);
    const endScreen = projectToScreenMultiView(endWorld);

    // Calculate beam bounds including visual thickness (4px)
    const beamThickness = 4;
    const elementBounds = {
      left: Math.min(startScreen.x, endScreen.x) - beamThickness,
      right: Math.max(startScreen.x, endScreen.x) + beamThickness,
      top: Math.min(startScreen.y, endScreen.y) - beamThickness,
      bottom: Math.max(startScreen.y, endScreen.y) + beamThickness,
    };

    // Check if beam is COMPLETELY within selection bounds
    return (
      elementBounds.left >= bounds.left &&
      elementBounds.right <= bounds.right &&
      elementBounds.top >= bounds.top &&
      elementBounds.bottom <= bounds.bottom
    );
  };

  // Check if node is completely contained in rectangle (STAAD.Pro style)
  const isNodeCompletelyContained = (
    node: any,
    bounds: { left: number; right: number; top: number; bottom: number }
  ): boolean => {
    const worldPos = new THREE.Vector3(node.x, node.y, node.z);
    const screenPos = projectToScreenMultiView(worldPos);

    // Node visual size (8px radius)
    const nodeRadius = 8;
    const elementBounds = {
      left: screenPos.x - nodeRadius,
      right: screenPos.x + nodeRadius,
      top: screenPos.y - nodeRadius,
      bottom: screenPos.y + nodeRadius,
    };

    // Check if node is COMPLETELY within selection bounds
    return (
      elementBounds.left >= bounds.left &&
      elementBounds.right <= bounds.right &&
      elementBounds.top >= bounds.top &&
      elementBounds.bottom <= bounds.bottom
    );
  };

  // Enhanced selection function with workspace freezing and complete containment (STAAD.Pro style)
  const selectElementsInBoundsWithCamera = (
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    _isCtrlPressed: boolean = false, // Parameter kept for API compatibility, always uses additive selection
    mode: "nodes" | "beams" | "all" = "all"
  ) => {
    const selectedNodeIds: string[] = [];
    const selectedBeamIds: string[] = [];

    // Create selection bounds
    const bounds = {
      left: Math.min(minX, maxX),
      right: Math.max(minX, maxX),
      top: Math.min(minY, maxY),
      bottom: Math.max(minY, maxY),
    };

    // NOTE: We no longer clear selection here - always use additive selection
    // The selection is managed by the calling code

    // Test nodes for COMPLETE CONTAINMENT selection (STAAD.Pro style)
    if (mode === "nodes" || mode === "all") {
      nodes.forEach((node, index) => {
        const isCompletelyContained = isNodeCompletelyContained(node, bounds);

        // console.log(`Node ${index} (${node.id}): World: (${node.x}, ${node.y}, ${node.z}) -> Complete containment: ${isCompletelyContained}`);

        if (isCompletelyContained) {
          selectedNodeIds.push(node.id);
        }
      });
    }

    // Test beams for COMPLETE CONTAINMENT selection (STAAD.Pro style)
    if (mode === "beams" || mode === "all") {
      beams.forEach((beam, index) => {
        const isCompletelyContained = isBeamCompletelyContained(beam, bounds);

        // console.log(`Beam ${index} (${beam.id}): Complete containment: ${isCompletelyContained}`);

        if (isCompletelyContained) {
          selectedBeamIds.push(beam.id);
        }
      });
    }

    // Apply selection
    // ----- Apply selection (ALWAYS additive for Ctrl+drag) -----
    // Get current selection
    const currentNodes = modelStore?.selectedNodeIds || [];
    const currentBeams = modelStore?.selectedBeamIds || [];

    // Calculate final beam selection
    let finalBeamIds: string[] = [];
    if (mode === "beams" || mode === "all") {
      // Merge new beams with existing (additive)
      finalBeamIds = [...new Set([...currentBeams, ...selectedBeamIds])];
    } else {
      // Mode is 'nodes' only - keep existing beams unchanged
      finalBeamIds = currentBeams;
    }

    // Calculate final node selection - STRICTLY respect mode
    let finalNodeIds: string[] = [];
    if (mode === "nodes") {
      // NODES ONLY mode - only select nodes, don't touch beams or add endpoint nodes
      finalNodeIds = [...new Set([...currentNodes, ...selectedNodeIds])];
    } else if (mode === "beams") {
      // BEAMS ONLY mode - only select beams, keep existing nodes unchanged (no auto-add endpoints)
      finalNodeIds = currentNodes;
    } else {
      // ALL mode - select both and include beam endpoints for visual consistency
      const beamEndpointNodes = new Set<string>();
      finalBeamIds.forEach((beamId) => {
        const beam = beams.find((b) => b.id === beamId);
        if (beam) {
          beamEndpointNodes.add(beam.startNodeId);
          beamEndpointNodes.add(beam.endNodeId);
        }
      });
      finalNodeIds = [
        ...new Set([
          ...currentNodes,
          ...selectedNodeIds,
          ...Array.from(beamEndpointNodes),
        ]),
      ];
    }

    // Apply final selection using setState directly to avoid store's auto-endpoint behavior
    // Use direct setState to bypass selectMultipleBeams auto-endpoint behavior
    useModelStore.setState((state: any) => ({
      selectedBeamIds: finalBeamIds,
      selectedNodeIds: finalNodeIds,
      beams: state.beams.map((beam: any) => ({
        ...beam,
        isSelected: finalBeamIds.includes(beam.id),
      })),
      nodes: state.nodes.map((node: any) => ({
        ...node,
        isSelected: finalNodeIds.includes(node.id),
        color: finalNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
      })),
    }));

    // Store bounds for copy/paste validation
    if (selectedNodeIds.length > 0 || selectedBeamIds.length > 0) {
      (window as any).__lastSelectionBounds = bounds;
      console.log(
        "🎯 Stored selection bounds for copy/paste validation:",
        bounds
      );
    }

    console.log("🎯 Complete containment selection completed:", {
      view: currentView,
      nodes: selectedNodeIds.length,
      beams: selectedBeamIds.length,
      nodeIds: selectedNodeIds,
      beamIds: selectedBeamIds,
    });
  };

  // Workspace control freezing functions
  const freezeWorkspace = () => {
    if (controls) {
      (controls as any).enabled = false;
      console.log("🧊 Workspace frozen - 3D controls disabled");
    }
  };

  const unfreezeWorkspace = () => {
    if (controls) {
      (controls as any).enabled = true;
      console.log("🔓 Workspace unfrozen - 3D controls restored");
    }
  };

  // Expose enhanced functions globally
  useEffect(() => {
    const handler = (
      minX: number,
      maxX: number,
      minY: number,
      maxY: number,
      isCtrlPressed: boolean = false,
      mode: "nodes" | "beams" | "all" = "all"
    ) => {
      selectElementsInBoundsWithCamera(
        minX,
        maxX,
        minY,
        maxY,
        isCtrlPressed,
        mode
      );
    };

    (window as any).selectElementsInBoundsWithCamera = handler;
    (window as any).freezeWorkspace = freezeWorkspace;
    (window as any).unfreezeWorkspace = unfreezeWorkspace;

    return () => {
      delete (window as any).selectElementsInBoundsWithCamera;
      delete (window as any).freezeWorkspace;
      delete (window as any).unfreezeWorkspace;
    };
  }, [
    camera,
    controls,
    nodes,
    beams,
    toggleNodeSelection,
    toggleBeamSelection,
    clearSelection,
    currentView,
  ]);

  // This is an invisible component that provides enhanced camera-aware selection
  return null;
};
